import { DateTime } from 'luxon'

export const formatDate = (date: string | Date | undefined, format?: string): string => {
  let dateTime

  if (date === undefined) return ''

  if (typeof date === 'string') {
    // Parse the string date using fromISO
    dateTime = DateTime.fromISO(date)
  } else if (date instanceof Date) {
    // Parse the JS Date object using fromJSDate
    dateTime = DateTime.fromJSDate(date)
  } else {
    throw new Error('Invalid date format')
  }

  // Set the locale and format the date
  return dateTime.setLocale('fr').toFormat(format || 'dd MMMM yyyy')
}

// Helper to format time (e.g., "08:30:00" to "8h30")
export const formatTime = (time: string): string => {
  // Extract hours and minutes assuming format HH:MM(:SS...)
  const [hours, minutes] = time.split(':')
  return `${parseInt(hours, 10)}h${minutes}`
}

// utils/formatFrenchDate.js
export function formatMessagingDate(date) {
  // Convert input to Date object if necessary
  const d = typeof date === 'string' ? new Date(date) : date

  // Get today's date at midnight
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  // Create a date object for the input date at midnight
  const inputDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())

  // Compute yesterday's date
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  // Check if the input date matches today or yesterday
  if (inputDate.getTime() === today.getTime()) {
    return "Aujourd'hui"
  } else if (inputDate.getTime() === yesterday.getTime()) {
    return 'Hier' // Lowercase as typically used in French, adjust capitalization if needed
  }

  // For other dates, proceed with full formatting
  const options: Intl.DateTimeFormatOptions = { weekday: 'long' }
  let weekdayFr = new Intl.DateTimeFormat('fr-FR', options).format(d)

  // Manually format numeric day, month, and year
  const day = ('0' + d.getDate()).slice(-2)
  const month = ('0' + (d.getMonth() + 1)).slice(-2)
  const year = d.getFullYear()

  // Capitalize the first letter of the weekday
  weekdayFr = weekdayFr.charAt(0).toUpperCase() + weekdayFr.slice(1)

  return `${weekdayFr} ${day}/${month}/${year}`
}

// Conversation-list style preview date: time if today, "Hier" if yesterday, else dd/mm/yyyy
export function formatPreviewDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const inputDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (inputDate.getTime() === today.getTime()) {
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }
  if (inputDate.getTime() === yesterday.getTime()) {
    return 'Hier'
  }

  const day = ('0' + d.getDate()).slice(-2)
  const month = ('0' + (d.getMonth() + 1)).slice(-2)
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

const normalizeTime = (t?: string) => {
  if (!t || t.trim() === '') return '00:00'
  if (t.length === 2) return `${t}:00`
  return t
}

export const toISO = (date: string, timeStart?: string): string => {
  const dt = DateTime.fromISO(date).startOf('day')

  const safeTime = normalizeTime(timeStart)

  const [hour, minute] = safeTime.split(':')

  return dt
    .set({
      hour: Number(hour),
      minute: Number(minute),
      second: 0,
      millisecond: 0,
    })
    .toUTC()
    .toISO()!
}

export const toUTCFromLocal = (date: string, time?: string) => {
  const safeTime = time?.trim() ? time : '00:00'

  const dt = DateTime.fromISO(`${date}T${safeTime}`, {
    zone: 'local',
  })

  return dt.toUTC().toISO()
}
